import axios, { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { ReferenceLocation } from '@/types';
import { POST_RESOLVE_LOCATIONS_API_ENDPOINT } from '@/constants';

type LocationInput = Pick<ReferenceLocation, 'type' | 'code' | 'name'> & { postcode?: string };

/** Stable key built from type+code+postcode so the query only re-fires when the set changes. */
export const resolveLocationsQueryKey = (locations: LocationInput[]): string =>
  locations
    .map((l) => `${l.type}:${l.code}:${l.postcode ?? ''}`)
    .sort()
    .join('|');

/**
 * Resolves a compact list of {type, code, postcode?} stubs (as stored in the URL)
 * into fully-enriched ReferenceLocation objects (admunitIds, latitude, longitude).
 *
 * - Fires only when the input list is non-empty and all items lack admunitIds
 *   (i.e. they came from the URL, not from the live autocomplete which already
 *   returns full data). This prevents a redundant call when the user picks
 *   locations via the dropdown.
 * - staleTime: Infinity — location reference data is static within a session.
 * - postcode is included only for PLACE locations (backend contract, PR #294).
 */
export const useResolveLocations = (locations: ReferenceLocation[] | undefined) => {
  const needsResolve =
    !!locations?.length && locations.every((l) => !l.admunitIds || l.admunitIds.length === 0);

  const input: LocationInput[] = (locations ?? []).map(({ type, code, name, postcode }) => ({
    type,
    code,
    name,
    ...(type === 'PLACE' && postcode ? { postcode } : {}),
  }));

  return useQuery<ReferenceLocation[], AxiosError>({
    queryKey: ['resolve-locations', resolveLocationsQueryKey(input)],
    queryFn: () =>
      axios
        .post<ReferenceLocation[]>(POST_RESOLVE_LOCATIONS_API_ENDPOINT, input)
        .then((res) => res.data),
    enabled: needsResolve,
    retry: false,
  });
};
